import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, CheckCircle2, Loader2, Upload } from 'lucide-react';
import { API_BASE, apiGet, apiPost, authedFetch } from '@/lib/api';
import { ContributionNotice } from '@/components/study/ContributionNotice';
import { ContributionThanksDialog } from '@/components/study/ContributionThanksDialog';
import { HowToEarnDialog } from '@/components/study/HowToEarnDialog';
import BuddyCorner from '@/components/common/BuddyCorner';

export function SyllabusUploadDialog({ open, onOpenChange, file, defaults = {}, onFiled }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profileNote, setProfileNote] = useState('');
  const [phase, setPhase] = useState('pick');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [rights, setRights] = useState(false);
  const [notes, setNotes] = useState('');
  const [thanks, setThanks] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [picked, setPicked] = useState(null);
  const input = useRef(null);
  const run = useRef(0);
  const upload = useCallback(async f => {
    const ticket = ++run.current;
    setPicked(f); setPhase('reading'); setError(''); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', f);
      const res = await authedFetch(`${API_BASE}/syllabus/ai/analyse`, { method: 'POST', body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'We could not read that file. Please try again.');
      if (run.current !== ticket) return;
      setResult(body); setPhase('verdict');
    } catch (e) { if (run.current === ticket) { setError(e.message); setPhase('pick'); } }
  }, []);
  useEffect(() => {
    if (!open) return;
    setSubject(defaults.subject || ''); setError(''); setResult(null); setPhase('pick'); setPicked(null);
    setRights(false); setNotes(''); setThanks(false); setProfileNote('');
    // Don't ask for subjects until we know the child's board and class. Asking
    // with blanks returns "complete your board, class, section and school
    // details first", which is both wrong and alarming when the profile is
    // perfectly complete and simply hasn't arrived yet.
    if (user?.board && String(user?.class_level || '')) {
      apiGet(`/onboarding/subjects?class_level=${user.class_level}&board=${encodeURIComponent(user.board)}`)
        .then(d => { setSubjects(d.subjects || []); setProfileNote(''); })
        // A missing profile is not an upload failure — it just means we cannot
        // suggest subjects yet, so say so quietly and offer the way to fix it.
        .catch(e => setProfileNote(e.message));
    }
    if (file) upload(file);
    return () => { run.current += 1; };
  }, [open, file, defaults.subject, user?.board, user?.class_level, upload]);  const submit = async () => {
    setPhase('saving'); setError('');
    try {
      await apiPost(`/school-mapping/uploads/${result.analysis_id}/submit`,
        { subject: subject.trim(), rights_declared: rights, notes: notes.trim() });
      setPhase('done'); setThanks(true); onFiled?.();
      window.dispatchEvent(new Event('school-mapping-updated'));
    } catch (e) { setError(e.message); setPhase('verdict'); }
  };
  return <>
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="syllabus-upload-dialog">
      <DialogTitle>Your school syllabus</DialogTitle>
      <DialogDescription data-testid="syllabus-upload-context" className="break-words">{user?.board} · Class {user?.class_level} · {user?.school}, {user?.city}</DialogDescription>
      <p data-testid="syllabus-wait-message" className="text-sm text-muted-foreground">Great things take a little time. We’ll review your syllabus and let you know when your study materials are ready.</p>
      {!(user?.board || '').toLowerCase().includes('cbse') && <ContributionNotice board={user?.board} compact />}
      {phase !== 'done' && <>
        <label className="text-xs space-y-1">Subject
          <Input data-testid="syllabus-subject-input" list="school-upload-subjects" value={subject} placeholder="Select or enter subject" maxLength={120} onChange={e => setSubject(e.target.value)} />
          <datalist id="school-upload-subjects">{subjects.map(s => <option key={s.name} value={s.name} />)}</datalist>
        </label>
        <input ref={input} type="file" data-testid="syllabus-file-input" className={`w-full min-w-0 text-xs ${picked ? 'hidden' : ''}`} accept=".pdf,.docx,.png,.jpg,.jpeg,.webp" disabled={phase === 'reading' || phase === 'saving'} onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); }} />
        {picked ? (
          <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5" data-testid="syllabus-file-chosen">
            You have uploaded <span className="font-heading text-foreground break-all">{picked.name}</span>
            <button type="button" data-testid="syllabus-change-file" onClick={() => input.current?.click()}
              className="underline">choose a different file</button>
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">PDF, DOCX or a clear photo · up to 12 MB</p>
        )}
        {profileNote && (
          <p className="text-xs text-amber-700 flex flex-wrap items-center gap-1.5" data-testid="syllabus-profile-note">
            We can suggest subjects once your board, class and school are saved.
            <button type="button" data-testid="syllabus-fix-profile"
              onClick={() => { onOpenChange(false); navigate('/profile'); }} className="underline font-heading">
              Add them now
            </button>
          </p>
        )}
      </>}
      {phase === 'reading' && <div data-testid="syllabus-reading" className="flex items-center gap-3 py-5"><Loader2 className="animate-spin h-5 w-5" />Checking readability and reading your chapters…</div>}
      {error && <p data-testid="syllabus-error" role="alert" className="text-sm text-destructive">{error}</p>}
      {phase === 'pick' && error && picked && <Button data-testid="syllabus-retry-btn" variant="outline" onClick={() => upload(picked)}>Try reading again</Button>}
      {result && ['verdict', 'saving'].includes(phase) && <>
        <p data-testid={result.accepted ? 'syllabus-accepted' : 'syllabus-rejected'} className="flex gap-2 text-sm">{result.accepted ? <CheckCircle2 className="shrink-0 h-4 w-4 text-emerald-600" /> : <AlertTriangle className="shrink-0 h-4 w-4 text-amber-600" />}{result.message}</p>
        {(result.unreadable_pages || []).map((p, i) => <p data-testid={`syllabus-unreadable-${i}`} key={i} className="text-xs text-amber-700">Page {p.page}: {p.reason}</p>)}
        {(result.warnings || []).map((w, i) => <p data-testid={`syllabus-warning-${i}`} key={i} className="text-xs text-amber-700">{w}</p>)}
        {result.accepted && <>
          <div data-testid="syllabus-preview" className="max-h-48 overflow-y-auto border-y py-3 space-y-2">{result.chapters.map((ch, i) => <div key={i} className="text-sm"><strong>{ch.name}</strong><p className="text-xs text-muted-foreground">{ch.topics.map(t => t.name).join(' · ')}</p></div>)}</div>
          <label className="flex items-start gap-2 text-xs py-1" data-testid="syllabus-rights-label">
            <input type="checkbox" data-testid="syllabus-rights-checkbox" checked={rights}
              onChange={e => setRights(e.target.checked)} className="mt-0.5" />
            <span className="text-muted-foreground">
              These pages are from a book I own and paid for — not downloaded from somewhere on the
              internet. I understand a book being available online does not grant permission to
              redistribute it, and that review by Gurukul Veda is not permission to redistribute
              copyrighted material.{' '}
              <button type="button" data-testid="syllabus-policy-link"
                onClick={() => setHowOpen(true)} className="underline font-heading">
                Read the upload policy
              </button>
            </span>
          </label>
          <label className="text-xs space-y-1 block">Anything we should know? (optional)
            <Input data-testid="syllabus-notes-input" value={notes} maxLength={1000}
              placeholder="e.g. Chapter 4 is missing from the school list"
              onChange={e => setNotes(e.target.value)} />
          </label>
          <Button data-testid="syllabus-confirm-btn" disabled={!subject.trim() || !rights || phase === 'saving'} onClick={submit}>{phase === 'saving' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Submit for admin review</Button>
        </>}
        {!result.accepted && <Button data-testid="syllabus-replace-file" variant="outline" onClick={() => input.current?.click()}>Upload a clean copy</Button>}
      </>}
      <BuddyCorner inline size={56} mood="curious"
        line="Photograph the pages from your own book and I'll read them with you — our team checks it, then your material appears." />
      {phase === 'done' && <div data-testid="syllabus-done" className="space-y-4 py-3"><CheckCircle2 className="h-8 w-8 text-emerald-600" /><p data-testid="syllabus-review-pending">Your {subject} syllabus is awaiting admin review. Study Buddy will let you know when approved materials are available.</p><Button data-testid="syllabus-done-close" onClick={() => onOpenChange(false)}>Back to Home</Button></div>}
    </DialogContent>
  </Dialog>
  <ContributionThanksDialog open={thanks} onOpenChange={setThanks}
    chapters={(result?.chapters || []).length} coins={100} />
  <HowToEarnDialog open={howOpen} onOpenChange={setHowOpen} />
  </>;
}
export default SyllabusUploadDialog;